/**
 * constants/researchVariables.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Every variable the Research Dashboard tracks, in one place. The squares
 * grid (ResearchDashboard.jsx) renders one tile per entry here; each tile
 * links to /research/variable/:id, which reads the matching entry to know
 * what to fetch, what axis to draw, and what to say about itself.
 *
 * Adding a future variable means adding one object below — not a new page,
 * not a new route. The id must match a key GET /api/research/group/:variable
 * understands (see routes/research.js's VARIABLE_MAP plus its srt-kosha /
 * quickscreener-domain / srt-domain-band / quickscreener-domain-flag /
 * quickscreener-recommendation / progress-indicator special cases).
 *
 * `description` and `methodology` are deliberately factual/procedural only
 * — what the number is and how it's computed. Neither field should ever
 * read like a finding or a conclusion about any child or group; the Hard
 * Rules in routes/research.js exist precisely so that judgement stays with
 * the person looking at the chart, not baked into the page.
 */

export const FAMILIES = {
    SRT:              { label: 'Full SRT',          color: '#9333EA' },
    QUICKSCREENER:    { label: 'QuickScreener',      color: '#16A34A' },
    PROGRESS:         { label: 'Progress Tracker',   color: '#2563EB' },
    ENGINE:           { label: 'Learning Engine',    color: '#EA580C' },
  };
  
  // The 11 functional clusters from constants/developmentTaxonomy.json,
  // hardcoded here rather than fetched, since the frontend only needs
  // id/label/icon/a display color — never the raw signal-ID membership lists,
  // which stay a backend-only computation detail (see computeClusterScores
  // in routes/research.js). Update this list if the taxonomy file's
  // qsClusters ever changes shape.
  export const QS_CLUSTERS = [
    { id: 'gross_motor',         label: 'Gross Motor & Coordination',           icon: '🏃', color: '#1B8A5A' },
    { id: 'fine_motor',          label: 'Fine Motor & Tool Use',                icon: '✏️', color: '#2563EB' },
    { id: 'visual_expression',   label: 'Drawing & Visual Representation',      icon: '🎨', color: '#9333EA' },
    { id: 'emotional_awareness', label: 'Emotional Awareness & Empathy',        icon: '💛', color: '#EAB308' },
    { id: 'social_regulation',   label: 'Social Skills & Self-Regulation',      icon: '🤝', color: '#DC2626' },
    { id: 'cognitive_reasoning', label: 'Cognitive Reasoning & Classification', icon: '🧠', color: '#7B2D8B' },
    { id: 'memory_sequencing',   label: 'Memory & Sequencing',                  icon: '🧩', color: '#0891B2' },
    { id: 'numeracy',            label: 'Early Numeracy',                       icon: '🔢', color: '#F57F17' },
    { id: 'literacy',            label: 'Emergent Literacy',                   icon: '📖', color: '#003366' },
    { id: 'language',            label: 'Language & Communication',           icon: '🗣️', color: '#B45309' },
    { id: 'creativity',          label: 'Creativity & Imagination',            icon: '🚀', color: '#475569' },
  ];
  
  export const TRACKED_VARIABLES = [
    // ── Full SRT ─────────────────────────────────────────────────────────
    {
      id: 'srt-kosha', label: 'SRT Kosha Score', family: 'SRT',
      unit: '% per Panchakosha domain', yDomain: [0, 100], needsKosha: true,
      description: 'The full School Readiness Test\'s per-domain percentage, from the 18 scored items in that Panchakosha domain.',
      methodology: 'Σ(item levels) ÷ (items attempted × 3) × 100, recomputed by the model on every save — never read as a stored snapshot.',
    },
    {
      id: 'srt-domain-band', label: 'SRT Domain Band', family: 'SRT',
      unit: 'Emerging / Developing / Achieved', yDomain: [0, 3], needsKosha: true,
      description: 'The same SRT domain score, classified into the three bands used in the validation deck.',
      methodology: 'Achieved ≥75 · Developing 45–74 · Emerging 1–44. A pure classification of the score above — nothing new is measured.',
    },
  
    // ── QuickScreener ────────────────────────────────────────────────────
    {
      id: 'quickscreener-domain', label: 'QuickScreener Domain Score', family: 'QUICKSCREENER',
      unit: '% per Panchakosha domain', yDomain: [0, 100], needsKosha: true,
      description: 'QuickScreener\'s own per-domain percentage, from its signal ratings for that round.',
      methodology: 'Stored directly on the SRTScreener record at the time it was conducted — not recomputed here.',
    },
    {
      id: 'quickscreener-domain-flag', label: 'QuickScreener Domain Flag', family: 'QUICKSCREENER',
      unit: 'Green / Amber / Red', yDomain: [0, 3], needsKosha: true,
      description: 'The colour badge QuickScreener already assigns to this domain for that round.',
      methodology: 'Surfaced exactly as stored — Green/Amber/Red is never recomputed from the score above; the two can in principle disagree slightly if the underlying rubric changes over time.',
    },
    {
      id: 'quickscreener-recommendation', label: 'QuickScreener Recommendation', family: 'QUICKSCREENER',
      unit: 'No-concern / Monitor / Full-SRT', yDomain: [0, 3],
      description: 'The overall recommendation from each QuickScreener round — one value per round, not per domain.',
      methodology: 'Stored directly on the SRTScreener record. Green/Amber/Red → No-concern/Monitor/Full-SRT, per the validation deck.',
    },
    {
      id: 'quickscreener-cluster', label: 'QuickScreener Cluster Score', family: 'QUICKSCREENER',
      unit: '% per functional cluster', yDomain: [0, 100], needsCluster: true,
      description: 'A different cut through the same 50 signals than the 5-kosha grouping above — 11 functional clusters (fine motor, emotional awareness, memory & sequencing, etc.) that mix kosha prefixes on purpose, per the taxonomy file.',
      methodology: 'Average of that cluster\'s rated signals (strong/present/emerging/absent → 3/2/1/0) as a % of the maximum possible — computed fresh from raw ratings every time, never stored.',
    },
  
    // ── Progress Tracker ─────────────────────────────────────────────────
    {
      id: 'progress-indicator', label: 'Progress Tracker Indicator', family: 'PROGRESS',
      unit: 'level (0–3)', yDomain: [0, 3], needsIndicator: true,
      description: 'One NCF-FS indicator\'s rating history — Not Observed / Emerging / Developing / Achieved.',
      methodology: 'Each rating is a single human judgement at a point in time, never averaged across indicators or recomputed from anything else.',
    },
  
    // ── Learning Engine (FlashcardSession) ──────────────────────────────
    {
      id: 'bkt-mastery', label: 'BKT Mastery Probability', family: 'ENGINE',
      unit: 'probability (0–1)', yDomain: [0, 1],
      description: 'Bayesian Knowledge Tracing\'s running estimate that the child has mastered this card.',
      methodology: 'Updated after every touch from pLearn/pSlip/pGuess — one line per card, never merged across cards.',
    },
    {
      id: 'bkt-slip', label: 'BKT Slip Rate', family: 'ENGINE',
      unit: 'probability (0–1)', yDomain: [0, 1],
      description: 'Probability of answering wrong despite having actually mastered the card.',
    },
    {
      id: 'bkt-guess', label: 'BKT Guess Rate', family: 'ENGINE',
      unit: 'probability (0–1)', yDomain: [0, 1],
      description: 'Probability of answering correctly without having actually mastered the card.',
    },
    {
      id: 'engine-mastered', label: 'Engine: Mastered Flag', family: 'ENGINE',
      unit: '1 = mastery threshold crossed', yDomain: [0, 1],
      description: 'Binary flag for the moment a card\'s mastery probability crosses the threshold below.',
      methodology: 'masteryProb ≥ 0.95, per the validation deck\'s Engine thresholds.',
    },
    {
      id: 'engine-guess-alert', label: 'Engine: Guess Alert', family: 'ENGINE',
      unit: '1 = alert raised', yDomain: [0, 1],
      description: 'Flags a card where the engine suspects the child is guessing more than the deck\'s threshold allows.',
      methodology: 'pGuess > 0.30, per the validation deck.',
    },
    {
      id: 'engine-slip-alert', label: 'Engine: Slip Alert', family: 'ENGINE',
      unit: '1 = alert raised', yDomain: [0, 1],
      description: 'Flags a card where the engine suspects careless mistakes despite real mastery.',
      methodology: 'pSlip > 0.10, per the validation deck.',
    },
    {
      id: 'stuck-alert', label: 'Stuck Alert', family: 'ENGINE',
      unit: '1 = flagged stuck', yDomain: [0, 1],
      description: 'A working heuristic for a card that isn\'t progressing — not a clinical standard, and not sourced from the validation deck.',
      methodology: 'masteryProb has stayed under 0.5 across the last 5 consecutive touches of this card. Easy to retune or replace once a real definition exists.',
    },
    {
      id: 'sm2-ef', label: 'SM-2 Ease Factor', family: 'ENGINE',
      unit: 'multiplier (floor 1.3)', yDomain: [1.3, 4],
      description: 'SM-2\'s ease factor for this card — higher means the spacing between reviews grows faster.',
    },
    {
      id: 'sm2-interval', label: 'SM-2 Interval', family: 'ENGINE',
      unit: 'days until next review',
      description: 'The spaced-repetition gap SM-2 has scheduled before this card comes up again.',
    },
    {
      id: 'elo-gap', label: 'Elo Rating Gap', family: 'ENGINE',
      unit: 'child rating − card difficulty',
      description: 'How far above or below the card\'s difficulty the child\'s Elo rating currently sits — positive means the card should feel easy.',
    },
    {
      id: 'module-accuracy', label: 'Module Accuracy', family: 'ENGINE',
      unit: '% per session', yDomain: [0, 100],
      description: 'The percentage of rated cards answered correctly, one point per session — session-level, not card-level.',
    },
  ];
  
  export const VARIABLE_BY_ID = Object.fromEntries(TRACKED_VARIABLES.map(v => [v.id, v]));